import React, { useCallback, useRef, useState } from 'react'

import { FileText } from 'lucide-react'
import { Control, Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from '@/components/common/Dialog'
import Spinner from '@/components/common/Spinner'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/common/Tabs'
import FileUpload, { CompactLocalFile } from '@/components/files/FileUpload'
import { sanitizeStringArray, toNull } from '@/components/library-v2/lib'
import {
  AffiliationTypeToggle,
  FORM_SET_VALUE_OPTIONS,
  getDefaultAffiliationType,
  PersonOrganizationFields,
  type PrimaryAffiliationType,
} from '@/components/library-v2/people/Form'
import {
  getPersonFields,
  PERSON_FORM_SECTION_CONFIGS,
  type PersonField,
} from '@/components/library-v2/people/Form/personFormSections'
import { BasicBlueButton } from '@/components/library-v2/ui/Button'
import { FileCombobox } from '@/components/library-v2/ui/Combobox'
import {
  CreateEntityDialogLayout,
  CreateEntityFieldShell,
  type CreateEntityDialogSection,
} from '@/components/library-v2/ui/CreateEntityDialogLayout'
import { FieldInput } from '@/components/library-v2/ui/Merge'
import { toEntityIsoDateTime } from '@/components/library-v2/ui/Merge/dateField'

import { useAgencies } from '@/hooks/library/useAgencies'
import { useCompanies } from '@/hooks/library/useCompanies'
import { useCreatePerson } from '@/hooks/library/useCreatePerson'
import { useCompanyDepartments } from '@/hooks/library/useDepartments'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationFiles } from '@/hooks/organization/useOrganizationFiles'
import { useUploadOrganizationFiles } from '@/hooks/organization/useUploadOrganizationFiles'

import type { UnhydratedFile as LibraryFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type {
  CreateEntityPerson,
  EntityAgency,
  EntityCompany,
  EntityCompanyDepartment,
  EntityPerson,
} from '@/types/library/Entity'

type ResumeMode = 'select' | 'upload'

interface OrganizationSectionBodyProps {
  affiliationType: PrimaryAffiliationType
  companyId: UUID | null
  agencyId: UUID | null
  departmentId: UUID | null
  companies?: EntityCompany[]
  agencies?: EntityAgency[]
  departments?: EntityCompanyDepartment[]
  onCompanyChange: (companyId: UUID) => void
  onAgencyChange: (agencyId: UUID) => void
  onDepartmentChange: (departmentId: UUID) => void
  onClearCompany: () => void
  onClearAgency: () => void
  onClearDepartment: () => void
}

const OrganizationSectionBody: React.FC<OrganizationSectionBodyProps> = ({
  affiliationType,
  companyId,
  agencyId,
  departmentId,
  companies,
  agencies,
  departments,
  onCompanyChange,
  onAgencyChange,
  onDepartmentChange,
  onClearCompany,
  onClearAgency,
  onClearDepartment,
}) => (
  <PersonOrganizationFields
    affiliationType={affiliationType}
    companyId={companyId}
    agencyId={agencyId}
    departmentId={departmentId}
    companies={companies}
    agencies={agencies}
    departments={departments}
    onCompanyChange={onCompanyChange}
    onAgencyChange={onAgencyChange}
    onDepartmentChange={onDepartmentChange}
    onClearCompany={onClearCompany}
    onClearAgency={onClearAgency}
    onClearDepartment={onClearDepartment}
  />
)

interface FieldsSectionBodyProps {
  fields: PersonField[]
  control: Control<CreateEntityPerson>
  agencies?: EntityAgency[]
  companies?: EntityCompany[]
  departments?: EntityCompanyDepartment[]
}

const FieldsSectionBody: React.FC<FieldsSectionBodyProps> = ({
  fields,
  control,
  agencies,
  companies,
  departments,
}) => (
  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
    {fields.map((field) => (
      <CreateEntityFieldShell
        key={field.key}
        label={field.label}
        required={field.required}
        fullWidth={field.type === 'textarea'}
      >
        <FieldInput
          field={field}
          control={control}
          agencies={agencies}
          companies={companies}
          departments={departments}
        />
      </CreateEntityFieldShell>
    ))}
  </div>
)

interface ResumeSectionBodyProps {
  control: Control<CreateEntityPerson>
  files?: LibraryFile[]
  resumeMode: ResumeMode
  fileToUpload: File | null
  hasResumeFile: boolean
  onResumeModeChange: (mode: string) => void
  onClearResume: () => void
  onAddFiles: (files: File[]) => void
  onRemoveFile: () => void
}

const ResumeSectionBody: React.FC<ResumeSectionBodyProps> = ({
  control,
  files,
  resumeMode,
  fileToUpload,
  hasResumeFile,
  onResumeModeChange,
  onClearResume,
  onAddFiles,
  onRemoveFile,
}) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
        Resume
      </span>
      {hasResumeFile ? (
        <BasicBlueButton
          type="button"
          onClick={onClearResume}
          className="text-sm"
        >
          Reset
        </BasicBlueButton>
      ) : null}
    </div>
    <Tabs
      value={resumeMode}
      onValueChange={onResumeModeChange}
      variant="grouped"
    >
      <TabsList>
        <TabsTrigger value="select">Select Existing</TabsTrigger>
        <TabsTrigger value="upload">Upload New</TabsTrigger>
      </TabsList>
      <TabsContent value="select">
        <Controller
          name="resume_file_id"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FileCombobox value={value} onChange={onChange} files={files} />
          )}
        />
      </TabsContent>
      <TabsContent value="upload">
        <div className="space-y-2">
          <FileUpload
            onAddFiles={onAddFiles}
            maxFiles={1}
            className="dark:bg-default-dark dark:hover:bg-primary-900"
          />
          {fileToUpload && (
            <CompactLocalFile file={fileToUpload} onDelete={onRemoveFile} />
          )}
        </div>
      </TabsContent>
    </Tabs>
  </div>
)

interface CreatePersonDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  initialValues?: Partial<CreateEntityPerson>
  onCreated?: (person: EntityPerson) => void
}

const personDefaultValues: CreateEntityPerson = {
  entity_type: 'person',
  first_name: '',
  middle_name: null,
  last_name: '',
  preferred_name: null,
  suffixes: null,
  aliases: null,
  role: null,
  email: null,
  alternate_email: null,
  phone: null,
  mobile_phone: null,
  office_phone: null,
  linkedin_url: null,
  entity_company_id: null,
  entity_agency_id: null,
  entity_department_id: null,
  resume_file_id: null,
  description: null,
  user_id: null,
  is_internal: false,
  relationship_status: null,
  owner_user_id: null,
  priority: null,
  last_contacted_at: null,
  tag_ids: [],
}

const getDefaultValues = (
  initialValues?: Partial<CreateEntityPerson>,
): CreateEntityPerson => ({
  ...personDefaultValues,
  ...initialValues,
})

const sanitizePerson = (data: CreateEntityPerson): CreateEntityPerson => {
  const hasCompanyAffiliation = Boolean(data.entity_company_id)

  return {
    ...data,
    first_name: data.first_name.trim(),
    middle_name: toNull(data.middle_name),
    last_name: data.last_name.trim(),
    preferred_name: toNull(data.preferred_name),
    suffixes: sanitizeStringArray(data.suffixes),
    aliases: sanitizeStringArray(data.aliases),
    role: toNull(data.role),
    email: toNull(data.email),
    alternate_email: toNull(data.alternate_email),
    phone: toNull(data.phone),
    mobile_phone: toNull(data.mobile_phone),
    office_phone: toNull(data.office_phone),
    linkedin_url: toNull(data.linkedin_url),
    entity_company_id: data.entity_company_id || null,
    entity_agency_id: hasCompanyAffiliation
      ? null
      : data.entity_agency_id || null,
    entity_department_id: hasCompanyAffiliation
      ? data.entity_department_id || null
      : null,
    resume_file_id: data.resume_file_id || null,
    description: toNull(data.description),
    relationship_status: toNull(data.relationship_status),
    priority: toNull(data.priority),
    last_contacted_at: toEntityIsoDateTime(data.last_contacted_at),
    tag_ids: data.tag_ids ?? [],
  }
}

export const CreatePersonDialog: React.FC<CreatePersonDialogProps> = ({
  open,
  setOpen,
  initialValues,
  onCreated,
}) => {
  const { activeOrgId } = useActiveOrgId()

  const [resumeMode, setResumeMode] = useState<ResumeMode>('select')
  const [affiliationType, setAffiliationType] =
    useState<PrimaryAffiliationType>(() =>
      getDefaultAffiliationType(initialValues),
    )
  const [fileToUpload, setFileToUpload] = useState<File | null>(null)
  const uploadedResumeFileIdRef = useRef<UUID | null>(null)
  const companyAffiliationRef = useRef<{
    companyId: UUID | null
    departmentId: UUID | null
  }>({
    companyId: initialValues?.entity_company_id ?? null,
    departmentId: initialValues?.entity_department_id ?? null,
  })
  const agencyAffiliationRef = useRef<UUID | null>(
    initialValues?.entity_agency_id ?? null,
  )

  const { safePush } = useSafePush()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = useForm<CreateEntityPerson>({
    defaultValues: getDefaultValues(initialValues),
    mode: 'onChange',
  })

  const personValues = useWatch({ control })
  const companyId = personValues.entity_company_id ?? null
  const agencyId = personValues.entity_agency_id ?? null
  const departmentId = personValues.entity_department_id ?? null
  const companyDepartmentsCompanyId = companyId || ('' as UUID | '')
  const fileId = personValues.resume_file_id || ('' as UUID | '')

  const { data: companies } = useCompanies({
    orgId: activeOrgId,
    withConfirmed: true,
    withSuggested: false,
  })

  const { data: agencies } = useAgencies({
    orgId: activeOrgId,
    withConfirmed: true,
    withSuggested: false,
  })

  const { data: departments } = useCompanyDepartments({
    orgId: activeOrgId,
    companyId: companyDepartmentsCompanyId,
  })

  const { data: files } = useOrganizationFiles({
    orgId: activeOrgId,
  })

  const { mutateAsync: uploadFile, isPending: isUploadingFile } =
    useUploadOrganizationFiles({ orgId: activeOrgId })

  const { mutateAsync: createPerson, isPending } = useCreatePerson({
    orgId: activeOrgId,
  })

  const handleCreate = useCallback(
    async (data: CreateEntityPerson) => {
      try {
        const personData = sanitizePerson(data)

        // Check if selected company is "our company" (linked to current org)
        // If so, automatically mark the person as internal
        if (personData.entity_company_id && companies) {
          const selectedCompany = companies.find(
            (c) => c.id === personData.entity_company_id,
          )
          if (selectedCompany?.linked_organization_id === activeOrgId) {
            personData.is_internal = true
          }
        }

        if (uploadedResumeFileIdRef.current !== null) {
          personData.resume_file_id = uploadedResumeFileIdRef.current
          setValue('resume_file_id', uploadedResumeFileIdRef.current)
        }

        if (
          resumeMode === 'upload' &&
          fileToUpload &&
          !personData.resume_file_id
        ) {
          // since we're uploading a resume for a person file, assume that we don't need to
          // extract entities from the file;
          // TODO: auto create the relationship between the person and the file
          const response = await uploadFile({
            files: [fileToUpload],
            tag_ids: [],
            extract_entities: false,
            unrestricted: true,
          })
          if (response && response.length > 0) {
            uploadedResumeFileIdRef.current = response[0].id
            personData.resume_file_id = uploadedResumeFileIdRef.current
            setValue('resume_file_id', uploadedResumeFileIdRef.current)
          } else {
            throw new Error('File upload returned no files')
          }
        }

        await createPerson(personData, {
          onSuccess: (created) => {
            uploadedResumeFileIdRef.current = null
            companyAffiliationRef.current = {
              companyId: initialValues?.entity_company_id ?? null,
              departmentId: initialValues?.entity_department_id ?? null,
            }
            agencyAffiliationRef.current =
              initialValues?.entity_agency_id ?? null
            setFileToUpload(null)
            setResumeMode('select')
            setAffiliationType(getDefaultAffiliationType(initialValues))
            reset(getDefaultValues(initialValues))
            setOpen(false)
            onCreated?.(created)
            toast.success('Person added successfully!', {
              action: {
                label: 'View',
                onClick: () => {
                  safePush(`/orgs/${activeOrgId}/library/people/${created.id}`)
                },
              },
            })
          },
        })
      } catch (error) {
        console.error('Failed to add new person:', error)
        // Error is handled by the useCreatePerson hook
      }
    },
    [
      createPerson,
      setOpen,
      reset,
      setValue,
      resumeMode,
      fileToUpload,
      uploadFile,
      setFileToUpload,
      setResumeMode,
      onCreated,
      safePush,
      activeOrgId,
      companies,
      initialValues,
    ],
  )

  const handleClose = useCallback(
    (open: boolean) => {
      setOpen(open)
      if (!open) {
        uploadedResumeFileIdRef.current = null
        companyAffiliationRef.current = {
          companyId: initialValues?.entity_company_id ?? null,
          departmentId: initialValues?.entity_department_id ?? null,
        }
        agencyAffiliationRef.current = initialValues?.entity_agency_id ?? null
        setFileToUpload(null)
        setResumeMode('select')
        setAffiliationType(getDefaultAffiliationType(initialValues))
        reset(getDefaultValues(initialValues))
      }
    },
    [setOpen, reset, setFileToUpload, setResumeMode, initialValues],
  )

  const handleAddFiles = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        setFileToUpload(files[0])
      }
    },
    [setFileToUpload],
  )

  const handleRemoveFile = useCallback(() => {
    setFileToUpload(null)
  }, [setFileToUpload])

  const handleResumeModeChange = useCallback(
    (mode: string) => {
      setResumeMode(mode as ResumeMode)
    },
    [setResumeMode],
  )

  const handleClearResume = useCallback(() => {
    setValue('resume_file_id', null)
  }, [setValue])

  const handleAffiliationTypeChange = useCallback(
    (type: PrimaryAffiliationType) => {
      if (type === affiliationType) {
        return
      }

      if (affiliationType === 'company') {
        companyAffiliationRef.current = { companyId, departmentId }
      } else {
        agencyAffiliationRef.current = agencyId
      }

      setAffiliationType(type)
      if (type === 'company') {
        setValue(
          'entity_company_id',
          companyAffiliationRef.current.companyId,
          FORM_SET_VALUE_OPTIONS,
        )
        setValue(
          'entity_department_id',
          companyAffiliationRef.current.departmentId,
          FORM_SET_VALUE_OPTIONS,
        )
        setValue('entity_agency_id', null, FORM_SET_VALUE_OPTIONS)
        return
      }

      setValue('entity_company_id', null, FORM_SET_VALUE_OPTIONS)
      setValue('entity_department_id', null, FORM_SET_VALUE_OPTIONS)
      setValue(
        'entity_agency_id',
        agencyAffiliationRef.current,
        FORM_SET_VALUE_OPTIONS,
      )
    },
    [affiliationType, agencyId, companyId, departmentId, setValue],
  )

  const handleCompanyChange = useCallback(
    (selectedCompanyId: UUID) => {
      setAffiliationType('company')
      companyAffiliationRef.current = {
        companyId: selectedCompanyId,
        departmentId: null,
      }
      setValue('entity_company_id', selectedCompanyId, FORM_SET_VALUE_OPTIONS)
      setValue('entity_agency_id', null, FORM_SET_VALUE_OPTIONS)
    },
    [setValue],
  )

  const handleAgencyChange = useCallback(
    (selectedAgencyId: UUID) => {
      setAffiliationType('agency')
      agencyAffiliationRef.current = selectedAgencyId
      setValue('entity_agency_id', selectedAgencyId, FORM_SET_VALUE_OPTIONS)
      setValue('entity_company_id', null, FORM_SET_VALUE_OPTIONS)
      setValue('entity_department_id', null, FORM_SET_VALUE_OPTIONS)
    },
    [setValue],
  )

  const handleDepartmentChange = useCallback(
    (selectedDepartmentId: UUID) => {
      companyAffiliationRef.current = {
        companyId,
        departmentId: selectedDepartmentId,
      }
      setValue(
        'entity_department_id',
        selectedDepartmentId,
        FORM_SET_VALUE_OPTIONS,
      )
    },
    [setValue],
  )

  const handleClearCompany = useCallback(() => {
    companyAffiliationRef.current = { companyId: null, departmentId: null }
    setValue('entity_company_id', null, FORM_SET_VALUE_OPTIONS)
    setValue('entity_department_id', null, FORM_SET_VALUE_OPTIONS)
  }, [setValue])

  const handleClearAgency = useCallback(() => {
    agencyAffiliationRef.current = null
    setValue('entity_agency_id', null, FORM_SET_VALUE_OPTIONS)
  }, [setValue])

  const handleClearDepartment = useCallback(() => {
    companyAffiliationRef.current = { companyId, departmentId: null }
    setValue('entity_department_id', null, FORM_SET_VALUE_OPTIONS)
  }, [companyId, setValue])

  const sections: CreateEntityDialogSection[] = [
    ...PERSON_FORM_SECTION_CONFIGS.map((section) => ({
      id: section.id,
      label: section.label,
      description: section.description,
      icon: section.icon,
      required: section.required,
      headerAccessory:
        section.id === 'organization' ? (
          <AffiliationTypeToggle
            value={affiliationType}
            onChange={handleAffiliationTypeChange}
          />
        ) : undefined,
      children:
        section.id === 'organization' ? (
          <OrganizationSectionBody
            affiliationType={affiliationType}
            companyId={companyId}
            agencyId={agencyId}
            departmentId={departmentId}
            companies={companies}
            agencies={agencies}
            departments={departments}
            onCompanyChange={handleCompanyChange}
            onAgencyChange={handleAgencyChange}
            onDepartmentChange={handleDepartmentChange}
            onClearCompany={handleClearCompany}
            onClearAgency={handleClearAgency}
            onClearDepartment={handleClearDepartment}
          />
        ) : (
          <FieldsSectionBody
            fields={getPersonFields(section.fieldKeys)}
            control={control}
            agencies={agencies}
            companies={companies}
            departments={departments}
          />
        ),
    })),
    {
      id: 'resume',
      label: 'Resume',
      description: 'Attach an existing resume or upload a new one.',
      icon: FileText,
      children: (
        <ResumeSectionBody
          control={control}
          files={files}
          resumeMode={resumeMode}
          fileToUpload={fileToUpload}
          hasResumeFile={Boolean(fileId)}
          onResumeModeChange={handleResumeModeChange}
          onClearResume={handleClearResume}
          onAddFiles={handleAddFiles}
          onRemoveFile={handleRemoveFile}
        />
      ),
    },
  ]

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        aria-describedby={undefined}
        className="h-[90vh] max-h-[760px] w-full max-w-[1040px] flex flex-col p-0 overflow-hidden"
        overlayClassName="p-0"
      >
        <form
          onSubmit={handleSubmit(handleCreate)}
          className="flex flex-col flex-1 min-h-0"
        >
          <CreateEntityDialogLayout
            navLabel="Sections"
            title="Create Person"
            description="Capture a contact profile, organization context, and engagement details."
            sections={sections}
            footer={
              <DialogFooter className="flex justify-end p-5 border-t border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-900">
                <DialogClose asChild>
                  <Button
                    variant="outline-compact"
                    disabled={isPending || isUploadingFile}
                    type="button"
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  variant="primary-compact"
                  type="submit"
                  disabled={isPending || isUploadingFile || !isValid}
                >
                  {(isUploadingFile || isPending) && (
                    <>
                      <Spinner variant="primary-compact" className="w-4 h-4" />
                      <span>
                        {isUploadingFile ? 'Uploading resume...' : 'Adding...'}
                      </span>
                    </>
                  )}
                  {!isUploadingFile && !isPending && 'Create Person'}
                </Button>
              </DialogFooter>
            }
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
